import { queueTrackingEvent } from '@library/tracking-integration'

export const SHARE_SURFACES = {
  rafPage: 'raf-page',
  boostedRafPage: 'boosted-raf-page',
  orderConfirmation: 'order-confirmation',
  orderConfirmationMarket: 'order-confirmation-market',
  welcome: 'welcome',
  welcomeFirstBox: 'welcome-first-box',
} as const

export const SHARE_OUTCOMES = {
  initiated: 'initiated',
  completed: 'completed',
  dismissed: 'dismissed',
  failed: 'failed',
} as const

export type ShareSurface = typeof SHARE_SURFACES[keyof typeof SHARE_SURFACES]
export type ShareOutcome = typeof SHARE_OUTCOMES[keyof typeof SHARE_OUTCOMES]

export const RAF_SHARE_OUTCOME_ACTION = 'raf_share_outcome'

type NativeShareOutcomeEvent = {
  action: typeof RAF_SHARE_OUTCOME_ACTION
  properties: {
    surface: ShareSurface
    outcome: ShareOutcome
  }
}

type ShareNativelyArgs = {
  payload: ShareData
  surface: ShareSurface
  onFallback: () => void
  onOpened?: () => void
}

export const shareNatively = async ({
  payload,
  surface,
  onFallback,
  onOpened,
}: ShareNativelyArgs): Promise<void> => {
  if (!navigator.share) {
    onFallback()

    return
  }

  const report = (outcome: ShareOutcome) =>
    queueTrackingEvent<NativeShareOutcomeEvent>({
      action: RAF_SHARE_OUTCOME_ACTION,
      properties: { surface, outcome },
    })

  report(SHARE_OUTCOMES.initiated)
  onOpened?.()

  try {
    await navigator.share(payload)
    report(SHARE_OUTCOMES.completed)
  } catch (error) {
    // Dismissing the native sheet rejects with AbortError; anything else means the share itself
    // could not be performed, so the caller's own share options take over.
    if ((error as Error)?.name === 'AbortError') {
      report(SHARE_OUTCOMES.dismissed)

      return
    }

    report(SHARE_OUTCOMES.failed)
    onFallback()
  }
}
